import React from 'react';
import errorReporter from '@app/errorReporter';
import { message } from 'components';
import { AppointmentActionModalContext } from 'contexts';
import { AgentType } from 'modals/AppointmentActionModal/types';
import { syncBuyerShowings, syncListingShowings } from 'api/AlmanacController';
import { ES_DELAY } from './constants';
import { useSearchParams } from './hooks/useSearchParams';
import { useShowingsCalendarContext } from './ShowingsCalendarContext';
import {
  AgentCalendarMenu,
  ListingCalendarMenu,
  ShowingCalendarSearchParams,
} from './types';

/** Handles initial mount actions defined in URL after context is loaded */
const ShowingsCalendarInitialActionsWrapper: React.FC = ({ children }) => {
  const { getSearchParam, getArraySearchParams, deleteSearchParam } =
    useSearchParams<ShowingCalendarSearchParams>();
  const { openModal: openAppointmentActionModal } = React.useContext(
    AppointmentActionModalContext,
  );
  const { refetchPage, selectedMenuOption } = useShowingsCalendarContext();

  React.useEffect(() => {
    const [action, appointmentId, agent, toast, wait, calendarSyncRedirect] =
      getSearchParam(
        'action',
        'appointmentId',
        'agent',
        'toast',
        'wait',
        'calendarSyncRedirect',
      );
    const [appointmentIds, listingIds] = getArraySearchParams(
      'syncAppointmentIds',
      'syncListingIds',
    );

    const paramsToDelete: ShowingCalendarSearchParams[] = [];
    if (toast) {
      switch (toast) {
        case 'time_change_requested':
          message.success('Time change requested');
          break;
        case 'time_change_suggested':
          message.success('Time change suggested');
          break;
        case 'showing_confirmed':
          message.success('Showing confirmed');
          break;
        default:
          errorReporter.error(
            `Invalid 'toast' param for Showings Calendar: ${toast}`,
          );
      }
      paramsToDelete.push('toast');
    }

    if (wait) {
      // Artifical wait has occured already so we can delete it now
      paramsToDelete.push('wait');
    }
    if (paramsToDelete.length) {
      deleteSearchParam(...paramsToDelete);
    }

    if (calendarSyncRedirect === 'true') {
      try {
        if (
          selectedMenuOption === AgentCalendarMenu.Listing ||
          selectedMenuOption === ListingCalendarMenu.InPerson
        ) {
          syncListingShowings({
            appointmentIds,
            listingIds,
          });
        } else if (selectedMenuOption === AgentCalendarMenu.Buyer) {
          syncBuyerShowings({ appointmentIds });
        }
      } catch (err) {
        errorReporter.error(
          `Error syncing calendar appointment(s)/listing(s) after Almanac redirect: ${err}`,
        );
      }

      deleteSearchParam(
        'calendarSyncRedirect',
        'syncAppointmentIds',
        'syncListingIds',
      );
    }

    if (
      !action ||
      !appointmentId ||
      typeof action !== 'string' ||
      typeof appointmentId !== 'string'
    )
      return;
    if (
      agent &&
      (typeof agent !== 'string' || !['listing', 'buyer'].includes(agent))
    )
      return errorReporter.error(
        `Invalid 'agent' param for Showings Calendar: ${agent}. appointmentId: ${appointmentId}, action: ${action}`,
      );

    if (!['confirm', 'deny', 'cancel'].includes(action))
      return errorReporter.error(
        `Invalid 'action' param for Showings Calendar: ${action}. appointmentId: ${appointmentId}, agent: ${agent}`,
      );

    openAppointmentActionModal({
      appointmentId,
      agent: (agent as AgentType) || 'listing',
      action: action as 'confirm' | 'deny' | 'cancel',
      onConfirm: () => refetchPage(ES_DELAY.LARGE),
      onClose: () => deleteSearchParam('action', 'appointmentId'),
    });
  }, []);

  return <>{children}</>;
};

export default ShowingsCalendarInitialActionsWrapper;
